// useTableFilters.js
import { useState, useMemo, useCallback } from 'react';

export default function useTableFilters(data = [], columnsConfig = []) {
  const [filters, setFilters] = useState({}); // ← THIS is the only filters state now
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [currentColumn, setCurrentColumn] = useState(null);

  const uniqueValues = useMemo(() => {
  const result = {};

  columnsConfig.forEach(({ key, getValue, getLabel }) => {
    if (!key) return;

    const map = new Map();

    data.forEach((row) => {
      const rawValue = getValue ? getValue(row) : row[key];
      if (rawValue == null || rawValue === '') return;

      const label = getLabel ? getLabel(row) : String(rawValue);
      map.set(String(rawValue), label);
    });

    result[key] = [...map.entries()].map(([value, label]) => ({
      value,
      label,
    }));
  });

  return result;
}, [data, columnsConfig]);

  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply column filters
    Object.entries(filters).forEach(([colKey, selectedVals]) => {
      if (!selectedVals?.length) return;
      result = result.filter((row) => {
        const getVal = columnsConfig.find(c => c.key === colKey)?.getValue;
        const value = getVal ? getVal(row) : row[colKey];
        return selectedVals.includes(String(value ?? ''));
      });
    });

    // Global search
    if (searchTerm.trim()) {
  const term = searchTerm.toLowerCase();

  result = result.filter((row) =>
    columnsConfig.some(({ getLabel, key }) => {
      const text = getLabel
        ? getLabel(row)
        : row[key];

      return String(text ?? '')
        .toLowerCase()
        .includes(term);
    })
  );
}

    return result;
  }, [data, filters, searchTerm, columnsConfig]);

  const openFilter = useCallback((event, columnKey) => {
    setCurrentColumn(columnKey);
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const closeFilter = useCallback(() => {
    setFilterAnchorEl(null);
    setCurrentColumn(null);
  }, []);

  const toggleFilter = useCallback((value) => {
    if (!currentColumn) return;

    setFilters(prev => {
      const prevValues = prev[currentColumn] || [];
      const stringValue = String(value);

      const newValues = prevValues.includes(stringValue)
        ? prevValues.filter(v => v !== stringValue)
        : [...prevValues, stringValue];

      const next = { ...prev };
      if (newValues.length === 0) {
        delete next[currentColumn];
      } else {
        next[currentColumn] = newValues;
      }
      return next;
    });
  }, [currentColumn]);

  const clearColumnFilter = useCallback(() => {
    if (!currentColumn) return;
    setFilters(prev => {
      const copy = { ...prev };
      delete copy[currentColumn];
      return copy;
    });
    closeFilter();
  }, [currentColumn, closeFilter]);

  const isFilterActive = useCallback((columnKey) => {
    return !!filters[columnKey]?.length;
  }, [filters]);

  return {
    searchTerm,
    setSearchTerm,
    filteredData,
    uniqueValues,
    filterAnchorEl,
    setFilterAnchorEl,
    currentColumn,
    openFilter,
    closeFilter,
    toggleFilter,
    clearColumnFilter,
    isFilterActive,
    filters
  };
}